import type {
    TokenValueEncoded,
    TokenValueAmountEncoded,
    TokenValues,
    TokenValueStringEncoded
} from "@silversea-ssc/dotcom-read-model-types/Tokens"
import { CurrencyCode } from "@silversea-ssc/dotcom-read-model-types/Currency"
import { buildTokenValueAmount } from "@silversea-ssc/dotcom-read-model-types/Tokens"
import { AirFareOption, AirFareOptionDetail, Amount, D2DOption, SingleFareOptions } from "@ssc-booking/api-contracts"
import * as A from "fp-ts/Array"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { Option } from "fp-ts/Option"
import * as ROA from "fp-ts/ReadonlyArray"
import * as R from "fp-ts/Record"
import * as P from "fp-ts/Predicate"

export const mapContentTokens = (singleFareOptions: SingleFareOptions, currency: CurrencyCode): TokenValues => {
    const savingPerPerson = mapSavingPerPerson(singleFareOptions)
    const businessAirPriceRt = mapBusinessAirPriceRt(singleFareOptions)
    const tokens: Option<TokenValueEncoded>[] = [
        mapSavingPerSuite(singleFareOptions),
        savingPerPerson,
        mapNonUseAirCredit(savingPerPerson),
        mapCurrency(currency),
        businessAirPriceRt,
        mapAirPrice(businessAirPriceRt),
        mapAirPriceBusiness(businessAirPriceRt),
        mapDoor2Door(singleFareOptions),
    ]

    return F.pipe(
        tokens,
        A.compact,
        A.map((x): [string, TokenValueEncoded] => [x.tokenName, x]),
        R.fromEntries,
    )
}

const mapSavingPerSuite = ({ airFares }: SingleFareOptions): Option<TokenValueAmountEncoded> =>
    F.pipe(
        airFares,
        ROA.findFirst(AirFareOption.hasAirFare("CruiseOnly")),
        O.map(
            F.flow(
                // keep new line
                AirFareOption.totalAmount,
                Amount.opposite,
                tokenValueAmount("saving_per_suite"),
            ),
        ),
    )

const mapSavingPerPerson = ({ airFares }: SingleFareOptions): Option<TokenValueAmountEncoded> =>
    F.pipe(
        airFares,
        ROA.findFirst(AirFareOption.hasAirFare("CruiseOnly")),
        O.map(
            F.flow(
                AirFareOption.firstGuestDetail,
                AirFareOptionDetail.lensFromProp("amount").get,
                Amount.opposite,
                tokenValueAmount("saving_per_person"),
            ),
        ),
    )

const mapNonUseAirCredit = (x: Option<TokenValueAmountEncoded>) => F.pipe(x, O.map(alias("non_use_air_credit")))

const mapCurrency = (currency: CurrencyCode): Option<TokenValueStringEncoded> =>
    O.some({
        type: "string",
        tokenName: "currency",
        value: currency,
    })

const mapBusinessAirPriceRt = ({ airFares }: SingleFareOptions): Option<TokenValueAmountEncoded> =>
    F.pipe(
        airFares,
        ROA.findFirst(F.pipe(AirFareOption.hasAirFare("Business"), P.and(AirFareOption.isPromotional))),
        O.map(
            F.flow(
                AirFareOption.firstGuestDetail,
                AirFareOptionDetail.lensFromProp("amount").get,
                tokenValueAmount("air_price_business_rt"),
            ),
        ),
    )

const mapAirPrice = (x: Option<TokenValueAmountEncoded>) => F.pipe(x, O.map(F.flow(alias("air_price"), divideBy(2))))

const mapAirPriceBusiness = (x: Option<TokenValueAmountEncoded>) =>
    F.pipe(mapAirPrice(x), O.map(alias("air_price_business")))

const mapDoor2Door = ({ d2d }: SingleFareOptions): Option<TokenValueAmountEncoded> =>
    F.pipe(
        d2d,
        O.map(
            F.flow(
                //keep new line
                D2DOption.lensFromProp("amount").get,
                tokenValueAmount("door_to_door"),
            ),
        ),
    )

const tokenValueAmount =
    (tokenName: string) =>
    (amount: Amount): TokenValueAmountEncoded =>
        buildTokenValueAmount(tokenName, amount.currency, amount.value, "none")

const divideBy =
    (divider: number) =>
    (x: TokenValueAmountEncoded): TokenValueAmountEncoded => ({ ...x, amount: x.amount / divider })

const alias =
    (tokenName: string) =>
    <T extends TokenValueEncoded>(x: T): T => ({ ...x, tokenName })
